import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  CheckCircleOutline as CompleteIcon,
  ReplayOutlined as ReopenIcon,
  PersonOutline as PersonIcon,
  ScheduleOutlined as ScheduleIcon,
  SnoozeOutlined as SnoozeIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { Task, Priority, Customer } from '../../types';

interface TaskDrawerProps {
  open: boolean;
  task: Task | null;
  customers: Customer[] | undefined;
  onClose: () => void;
  onSave: (id: string, data: Partial<Task>) => void;
  onDelete: (id: string) => void;
  onToggleComplete: (task: Task) => void;
  onSnooze?: (task: Task, by: 'day' | 'week') => void;
  saving?: boolean;
  deleting?: boolean;
}

const priorityOptions: { value: Priority; label: string; color: string; tint: string }[] = [
  { value: 'LOW', label: 'Lav', color: '#5C6B63', tint: '#EDF0EE' },
  { value: 'MEDIUM', label: 'Medium', color: '#3E7A5A', tint: '#E7F2EC' },
  { value: 'HIGH', label: 'Høj', color: '#8A5A10', tint: '#FBF1DE' },
  { value: 'URGENT', label: 'Urgent', color: '#8A2E24', tint: '#FBEAE8' },
];

export const TaskDrawer: React.FC<TaskDrawerProps> = ({
  open,
  task,
  customers,
  onClose,
  onSave,
  onDelete,
  onToggleComplete,
  onSnooze,
  saving,
  deleting,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('MEDIUM');
  const [dueDate, setDueDate] = useState<Date>(new Date());
  const [customerId, setCustomerId] = useState('');

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || '');
      setPriority(task.priority);
      setDueDate(new Date(task.dueDate));
      setCustomerId(task.customerId || '');
    }
  }, [task]);

  if (!task) return null;

  const handleSave = () => {
    onSave(task.id, {
      title,
      description: description || undefined,
      priority,
      dueDate: dueDate.toISOString(),
      customerId: customerId || undefined,
    } as any);
  };

  const isDirty =
    title !== task.title ||
    (description || '') !== (task.description || '') ||
    priority !== task.priority ||
    new Date(dueDate).toISOString() !== task.dueDate ||
    (customerId || '') !== (task.customerId || '');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440 } } }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #E3ECE6',
          bgcolor: '#F6FAF7',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            fontSize: '0.9rem',
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          Opgave
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        <Stack spacing={2}>
          <TextField
            label="Titel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
            required
          />

          <TextField
            label="Beskrivelse"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={3}
          />

          <DateTimePicker
            label="Forfald"
            value={dueDate}
            onChange={(d) => d && setDueDate(d)}
            ampm={false}
            format="dd. MMM yyyy HH:mm"
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Prioritet</InputLabel>
            <Select
              value={priority}
              label="Prioritet"
              onChange={(e) => setPriority(e.target.value as Priority)}
            >
              {priorityOptions.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Kunde</InputLabel>
            <Select
              value={customerId}
              label="Kunde"
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <MenuItem value="">Ingen</MenuItem>
              {customers?.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!task.isCompleted && onSnooze && (
            <Box>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  fontWeight: 700,
                  color: 'text.secondary',
                  mb: 0.75,
                }}
              >
                Udskyd
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SnoozeIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onSnooze(task, 'day')}
                  sx={{ fontSize: '0.7rem' }}
                >
                  +1 dag
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SnoozeIcon sx={{ fontSize: 14 }} />}
                  onClick={() => onSnooze(task, 'week')}
                  sx={{ fontSize: '0.7rem' }}
                >
                  +1 uge
                </Button>
              </Stack>
            </Box>
          )}

          <Divider />

          <Box>
            <Typography
              sx={{
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontWeight: 700,
                color: 'text.secondary',
                mb: 1,
              }}
            >
              Metadata
            </Typography>
            <Stack spacing={0.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Oprettet{' '}
                  {format(new Date(task.createdAt), 'dd. MMM yyyy HH:mm', { locale: da })}
                </Typography>
              </Box>
              {task.createdBy && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Oprettet af {task.createdBy.firstName} {task.createdBy.lastName}
                  </Typography>
                </Box>
              )}
              {task.isCompleted && task.completedAt && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CompleteIcon sx={{ fontSize: 14, color: '#208040' }} />
                  <Typography variant="caption" sx={{ color: '#16602F', fontWeight: 600 }}>
                    Fuldført{' '}
                    {format(new Date(task.completedAt), 'dd. MMM yyyy HH:mm', { locale: da })}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
          {task.isCompleted && (
            <Chip
              size="small"
              label="Fuldført"
              sx={{
                alignSelf: 'flex-start',
                bgcolor: '#DDEEE2',
                color: '#16602F',
                fontWeight: 600,
              }}
            />
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid #E3ECE6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: '#FAFCFB',
        }}
      >
        <Button
          size="small"
          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
          onClick={() => onDelete(task.id)}
          disabled={deleting}
          sx={{ color: '#8A2E24', '&:hover': { bgcolor: '#FBEAE8' } }}
        >
          Slet
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              task.isCompleted ? (
                <ReopenIcon sx={{ fontSize: 16 }} />
              ) : (
                <CompleteIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={() => onToggleComplete(task)}
          >
            {task.isCompleted ? 'Gen-åbn' : 'Fuldfør'}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={!isDirty || saving || !title.trim()}
          >
            {saving ? 'Gemmer…' : 'Gem'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default TaskDrawer;
